/**
 * Version History Modal
 *
 * Displays parsed versions.md content in a styled modal dialog.
 * Content is embedded at build time via Vite's ?raw import.
 */

import { useMemo } from 'react';
import versionsRaw from '../../../../versions.md?raw';

interface VersionEntry {
  version: string;
  date: string;
  sections: {
    type: 'nieuwe-functionaliteiten' | 'verbeteringen' | 'bugfixes';
    label: string;
    items: { title: string; bullets: string[] }[];
  }[];
}

function parseVersions(raw: string): VersionEntry[] {
  const entries: VersionEntry[] = [];

  // Split by --- separators
  const blocks = raw.split(/\n---\n/).filter((b) => b.trim());

  for (const block of blocks) {
    const lines = block.split('\n');

    // Find version line: {versienummer} X.X.X - {versie datum} YYYY-MM-DD
    const versionLine = lines.find((l) =>
      l.match(/\{versienummer\}\s+[\d.]+/)
    );
    if (!versionLine) continue;

    const versionMatch = versionLine.match(
      /\{versienummer\}\s+([\d.]+)\s*-\s*\{versie datum\}\s*(\S+)/
    );
    if (!versionMatch || !versionMatch[1] || !versionMatch[2]) continue;

    const version = versionMatch[1];
    const date = versionMatch[2];

    const entry: VersionEntry = { version, date, sections: [] };

    let currentSection: VersionEntry['sections'][number] | null = null;
    let currentItem: { title: string; bullets: string[] } | null = null;

    for (const line of lines) {
      // Skip version line, title line, and template tags
      if (line.match(/\{versienummer\}/) || line.match(/^#\s/) || line.match(/\{vorige versie\}/)) continue;

      // Section headers
      if (line.match(/^##\s+Nieuwe Functionaliteiten/)) {
        currentSection = { type: 'nieuwe-functionaliteiten', label: 'Nieuwe Functionaliteiten', items: [] };
        entry.sections.push(currentSection);
        currentItem = null;
        continue;
      }
      if (line.match(/^##\s+Verbeteringen/)) {
        currentSection = { type: 'verbeteringen', label: 'Verbeteringen', items: [] };
        entry.sections.push(currentSection);
        currentItem = null;
        continue;
      }
      if (line.match(/^##\s+Bugfixes/)) {
        currentSection = { type: 'bugfixes', label: 'Bugfixes', items: [] };
        entry.sections.push(currentSection);
        currentItem = null;
        continue;
      }

      if (!currentSection) continue;

      // Bold title: **Title**
      const titleMatch = line.match(/^\*\*(.+)\*\*$/);
      if (titleMatch && titleMatch[1]) {
        currentItem = { title: titleMatch[1], bullets: [] };
        currentSection.items.push(currentItem);
        continue;
      }

      // Bullet point: - text
      const bulletMatch = line.match(/^-\s+(.+)/);
      if (bulletMatch && bulletMatch[1] && currentItem) {
        currentItem.bullets.push(bulletMatch[1]);
      }
    }

    if (entry.sections.length > 0) {
      entries.push(entry);
    }
  }

  return entries;
}

function formatDate(dateStr: string): string {
  const months = [
    'januari', 'februari', 'maart', 'april', 'mei', 'juni',
    'juli', 'augustus', 'september', 'oktober', 'november', 'december',
  ];
  const parts = dateStr.split('-').map(Number);
  const year = parts[0];
  const monthIdx = (parts[1] ?? 1) - 1;
  const day = parts[2];
  return `${day} ${months[monthIdx]} ${year}`;
}

const SECTION_COLORS = {
  'nieuwe-functionaliteiten': { header: 'text-green-700' },
  'verbeteringen': { header: 'text-blue-700' },
  'bugfixes': { header: 'text-red-700' },
} as const;

interface VersionHistoryModalProps {
  onClose: () => void;
}

export function VersionHistoryModal({ onClose }: VersionHistoryModalProps) {
  const entries = useMemo(() => parseVersions(versionsRaw), []);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-[50%] max-h-[85vh] bg-white rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-blue-600 rounded-t-lg">
          <h2 className="text-lg font-semibold text-white">Versiegeschiedenis</h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-6 space-y-8">
          {entries.map((entry) => (
            <div key={entry.version}>
              {/* Version badge + date */}
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 text-sm font-semibold text-blue-700 bg-blue-100 rounded-full">
                  v{entry.version}
                </span>
                <span className="text-sm text-gray-500">{formatDate(entry.date)}</span>
              </div>

              {/* Sections */}
              {entry.sections.map((section, sIdx) => {
                const colors = SECTION_COLORS[section.type];
                return (
                  <div key={sIdx} className="mb-4">
                    {/* Section header */}
                    <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${colors.header}`}>
                      {section.label}
                    </h3>

                    {/* Items */}
                    {section.items.map((item, iIdx) => (
                      <div key={iIdx} className="mb-3">
                        <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                        {item.bullets.length > 0 && (
                          <ul className="mt-1 ml-4 space-y-0.5">
                            {item.bullets.map((bullet, bIdx) => (
                              <li key={bIdx} className="text-sm text-gray-600 list-disc">
                                {bullet}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}

              {/* Separator between versions */}
              <hr className="border-gray-200 mt-6" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Returns the latest version number from versions.md */
export function getLatestVersion(): string {
  const match = versionsRaw.match(/\{versienummer\}\s+([\d.]+)/);
  return match?.[1] ?? '0.0.0';
}
